import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, Table, Alert, Form, InputGroup, Badge, Row, Col } from 'react-bootstrap';
import { useAdminMunicipalities } from 'api/hooks/use-admin-municipalities';
import { useAdminSections } from 'api/hooks/use-admin-sections';
import TableFooter from 'shared/tables/table-footer';
import TableLoading from 'shared/tables/table-loading';
import SortArrow from 'shared/tables/sort-arrow';
import { updateOrdering } from 'shared/tables/utils';

const COLUMNS = 10;

const SCHEMA_DRIFT_OPTIONS = [
  { value: 'drift', label: 'Out of date' },
  { value: 'clean', label: 'Up to date' },
  { value: 'not_checked', label: 'Not checked' }
];

const DATA_UPDATE_STATUS_OPTIONS = [
  { value: 'success', label: 'Success' },
  { value: 'error', label: 'Error' },
  { value: 'running', label: 'Running' },
  { value: 'none', label: 'No updates' }
];

const REFRESH_SCHEDULE_OPTIONS = [
  { value: 'scheduled', label: 'Any schedule' },
  { value: 'none', label: 'None (manual only)' },
  { value: 'nightly', label: 'Nightly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

// Data Status and SQL Schema are only meaningful for municipalities on an
// automatic refresh schedule. Mirror the backend `scheduled` scope
// (`where.not(refresh_schedule: 'none')`).
const isOnRefreshSchedule = (municipality) =>
  municipality.refresh_schedule && municipality.refresh_schedule !== 'none';

const MunicipalitiesList = () => {
  const [params, setParamsState] = useState({
    page: 1,
    perPage: 25,
    orderBy: 'name',
    orderDirection: 'asc',
    search: '',
    module: '',
    schema_drift: '',
    data_update_status: '',
    refresh_schedule: ''
  });

  const setParams = useCallback((newParams) => {
    setParamsState(prevParams =>
      typeof newParams === 'function' ? newParams(prevParams) : newParams
    );
  }, []);

  const { data: response, isLoading, isSuccess, error } = useAdminMunicipalities({ params });
  const { data: sections } = useAdminSections({ options: { refetchOnWindowFocus: false } });

  const municipalities = response?.data;
  const meta = response?.meta;

  const handleSearch = (e) => {
    setParams(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleFilter = (key) => (e) => {
    setParams(prev => ({ ...prev, [key]: e.target.value, page: 1 }));
  };

  const handleSort = (column) => {
    updateOrdering(column, params, setParams);
  };

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error loading municipalities</Alert.Heading>
        <p>Error message: {error.message}</p>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Municipalities</h2>
        <Button as={Link} to="/admin/municipalities/new" variant="primary">
          Create Municipality
        </Button>
      </div>

      <InputGroup className="mb-3">
        <InputGroup.Text>Search</InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Search by name, abbreviation, or email domain..."
          value={params.search}
          onChange={handleSearch}
        />
      </InputGroup>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Module</Form.Label>
            <Form.Select value={params.module} onChange={handleFilter('module')}>
              <option value="">All modules</option>
              {sections?.map((section) => (
                <option key={section.id} value={section.abbreviation}>
                  {section.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">SQL Schema</Form.Label>
            <Form.Select value={params.schema_drift} onChange={handleFilter('schema_drift')}>
              <option value="">All schema statuses</option>
              {SCHEMA_DRIFT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Data Status</Form.Label>
            <Form.Select value={params.data_update_status} onChange={handleFilter('data_update_status')}>
              <option value="">All data statuses</option>
              {DATA_UPDATE_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Refresh Schedule</Form.Label>
            <Form.Select value={params.refresh_schedule} onChange={handleFilter('refresh_schedule')}>
              <option value="">All</option>
              {REFRESH_SCHEDULE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th role="button" onClick={() => handleSort('name')}>
              Name <SortArrow column="name" orderBy={params.orderBy} orderDirection={params.orderDirection} />
            </th>
            <th role="button" onClick={() => handleSort('abbreviation')}>
              Abbreviation <SortArrow column="abbreviation" orderBy={params.orderBy} orderDirection={params.orderDirection} />
            </th>
            <th role="button" onClick={() => handleSort('email_domain')}>
              Email Domain <SortArrow column="email_domain" orderBy={params.orderBy} orderDirection={params.orderDirection} />
            </th>
            <th role="button" onClick={() => handleSort('directory')}>
              Directory <SortArrow column="directory" orderBy={params.orderBy} orderDirection={params.orderDirection} />
            </th>
            <th role="button" onClick={() => handleSort('computed_users_count')}>
              Users <SortArrow column="computed_users_count" orderBy={params.orderBy} orderDirection={params.orderDirection} />
            </th>
            <th>Top User</th>
            <th>Last User Activity</th>
            <th>Data Status</th>
            <th>SQL Schema</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isSuccess && municipalities?.map((municipality) => (
            <tr key={municipality.id}>
              <td>{municipality.name}</td>
              <td>{municipality.abbreviation}</td>
              <td>{municipality.email_domain}</td>
              <td>{municipality.directory}</td>
              <td>{municipality.users_count}</td>
              <td>
                {municipality.top_user ? (
                  <Link to={`/admin/users/${municipality.top_user.id}`}>
                    {municipality.top_user.name}
                  </Link>
                ) : (
                  <span className="text-muted">--</span>
                )}
              </td>
              <td>
                {municipality.last_user_activity_at
                  ? new Date(municipality.last_user_activity_at).toLocaleDateString()
                  : <span className="text-muted">No activity</span>
                }
              </td>
              <td>
                {!isOnRefreshSchedule(municipality) ? (
                  <span className="text-muted">--</span>
                ) : municipality.latest_data_update ? (
                  municipality.latest_data_update.has_error ? (
                    <Badge bg="danger" title={`Error in ${municipality.latest_data_update.function_name}`}>
                      Error
                    </Badge>
                  ) : municipality.latest_data_update.status_text === 'running' ? (
                    <Badge bg="warning">Running</Badge>
                  ) : (
                    <Badge bg="success">Success</Badge>
                  )
                ) : (
                  <Badge bg="secondary" text="primary">No Updates</Badge>
                )}
              </td>
              <td>
                {!isOnRefreshSchedule(municipality) ? (
                  <span className="text-muted">--</span>
                ) : municipality.sql_schema_check ? (
                  municipality.sql_schema_check.has_issues ? (
                    <Link to={`/admin/municipalities/${municipality.id}?tab=sql_schema`}>
                      <Badge
                        bg="danger"
                        title={`${municipality.sql_schema_check.total_missing_tables} missing table(s), ${municipality.sql_schema_check.total_missing_columns} missing column(s)`}
                      >
                        Out of date
                      </Badge>
                    </Link>
                  ) : (
                    <Badge bg="success">Up to date</Badge>
                  )
                ) : (
                  <Badge bg="light" text="primary">Not checked</Badge>
                )}
              </td>
              <td>
                <Button
                  as={Link}
                  to={`/admin/municipalities/${municipality.id}`}
                  variant="info"
                  size="sm"
                  className="me-2"
                >
                  View
                </Button>
                <Button
                  as={Link}
                  to={`/admin/municipalities/${municipality.id}/edit`}
                  variant="warning"
                  size="sm"
                >
                  Edit
                </Button>
              </td>
            </tr>
          ))}
          {isLoading && <TableLoading columns={COLUMNS} />}
        </tbody>
      </Table>

      {isSuccess && meta && (
        <TableFooter
          meta={meta}
          params={params}
          setParams={setParams}
        />
      )}

      {isSuccess && municipalities?.length === 0 && (
        <Alert variant="info">No municipalities found.</Alert>
      )}
    </div>
  );
};

export default MunicipalitiesList;
